import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { pushEvent, ANALYTICS_EVENTS } from "../../utils/analytics";
import { fetchWithAuth } from "../../utils/apiHelpers";
import { decodeBase64UrlJson, getOAuthError, getOAuthPayload } from "../../utils/oauthPayload";
import { createSavedBrokerCredentialFromOAuth } from "../../utils/savedSecrets";
import useUser from "../../hooks/useUser";
import "./SchwabSuccess.css";

function truncateAccountNumber(accountNumber) {
  const v = String(accountNumber || "");
  if (!v) return "";
  if (v.length <= 8) return v;
  return `••••${v.slice(-4)}`;
}

function readBrokerConnectionParams() {
  const saved = sessionStorage.getItem("schwab_oauth_broker_connection_params");
  if (!saved) return null;

  try {
    return JSON.parse(saved);
  } catch (_e) {
    return null;
  }
}

export default function SchwabSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getApiToken, isAuthenticated } = useUser();

  const payload = getOAuthPayload(location);
  const error = getOAuthError(location);

  const [copied, setCopied] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");

  const [deploying, setDeploying] = useState(false);
  const [encryptionError, setEncryptionError] = useState(null);

  const decoded = useMemo(() => (payload ? decodeBase64UrlJson(payload) : null), [payload]);
  const brokerConnectionParams = useMemo(() => readBrokerConnectionParams(), []);

  // Check if we came from deploy page
  const deployParams = useMemo(() => {
    const saved = sessionStorage.getItem("schwab_oauth_deploy_params");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (_e) {
        return null;
      }
    }
    return null;
  }, []);
  const isManagedBotSpotFlow = Boolean(brokerConnectionParams || deployParams);

  useEffect(() => {
    if (error === "expired") {
      pushEvent({ event: ANALYTICS_EVENTS.OAUTH_SCHWAB_EXPIRED, source: window.location.pathname });
    } else if (!payload) {
      pushEvent({ event: ANALYTICS_EVENTS.OAUTH_SCHWAB_LOADING, source: window.location.pathname });
    } else {
      pushEvent({ event: ANALYTICS_EVENTS.OAUTH_SCHWAB_SUCCESS, source: window.location.pathname });
    }
  }, [payload, error]);

  const loadAccounts = useCallback(async () => {
    if (!payload) return;

    setAccountsLoading(true);
    setAccountsError("");
    try {
      const token = await getApiToken();
      const resp = await fetchWithAuth(token, "/broker_oauth/schwab/accounts", {
        method: "POST",
        body: { payload },
      });
      const list = Array.isArray(resp?.accounts) ? resp.accounts : [];
      setAccounts(list);

      if (list.length === 1 && list[0]?.accountNumber) {
        setSelectedAccountNumber(String(list[0].accountNumber));
      }
    } catch (e) {
      console.error("[SchwabSuccess] Failed to load Schwab accounts", e);
      setAccountsError("Unable to load your Schwab accounts. Please try again.");
    } finally {
      setAccountsLoading(false);
    }
  }, [payload, getApiToken]);

  useEffect(() => {
    if (!isManagedBotSpotFlow) return;
    if (!payload) return;
    if (!isAuthenticated) return;
    loadAccounts();
  }, [isManagedBotSpotFlow, payload, isAuthenticated, loadAccounts]);

  const saveBrokerConnectionCredentials = useCallback(async () => {
    if (!payload || !selectedAccountNumber) return;

    const token = await getApiToken();
    await createSavedBrokerCredentialFromOAuth(token, {
      broker: "schwab",
      displayName: brokerConnectionParams?.displayName || undefined,
      paperOrLive: "live",
      externalAccountId: selectedAccountNumber,
      externalAccountLabel: truncateAccountNumber(selectedAccountNumber),
      providerMetadata: {
        accountNumber: selectedAccountNumber,
      },
      credentialValues: {
        SCHWAB_TOKEN: payload,
        SCHWAB_ACCOUNT_NUMBER: selectedAccountNumber,
      },
    });
    sessionStorage.removeItem("schwab_oauth_broker_connection_params");
    sessionStorage.removeItem("schwab_oauth_deploy_params");
  }, [brokerConnectionParams, getApiToken, payload, selectedAccountNumber]);

  // Auto-redirect when launched from /deploy and we have an account selection
  useEffect(() => {
    if (!payload || !selectedAccountNumber || !deployParams) return;

    let cancelled = false;
    const run = async () => {
      setDeploying(true);
      try {
        await saveBrokerConnectionCredentials();
        sessionStorage.removeItem("schwab_oauth_deploy_params");
        if (cancelled) return;

        setTimeout(() => {
          const deployUrl = `/deploy?strategyId=${deployParams.strategyId}&aiStrategyId=${deployParams.aiStrategyId}&aiStrategyRevisionId=${deployParams.aiStrategyRevisionId}`;
          navigate(deployUrl);
        }, 2000);
      } catch (e) {
        console.error("[SchwabSuccess] Error saving broker connection:", e);
        setEncryptionError("Failed to save Schwab connection. Please try again.");
        setDeploying(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [payload, selectedAccountNumber, deployParams, navigate, saveBrokerConnectionCredentials]);

  const handleCopy = async () => {
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2200);
    } catch (_e) {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = payload;
      textarea.style.position = "fixed";
      textarea.style.opacity = 0;
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2200);
      } catch {}
      document.body.removeChild(textarea);
    }

    pushEvent({ event: ANALYTICS_EVENTS.OAUTH_SCHWAB_COPY, source: window.location.pathname });
  };

  const handleCopyJson = async () => {
    if (!decoded) return;
    const jsonValue = JSON.stringify(decoded, null, 2);
    try {
      await navigator.clipboard.writeText(jsonValue);
      setJsonCopied(true);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2200);
    } catch (_e) {
      const textarea = document.createElement("textarea");
      textarea.value = jsonValue;
      textarea.style.position = "fixed";
      textarea.style.opacity = 0;
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        setJsonCopied(true);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2200);
      } catch {}
      document.body.removeChild(textarea);
    }

    pushEvent({
      event: ANALYTICS_EVENTS.OAUTH_SCHWAB_COPY,
      copyType: "json",
      source: window.location.pathname,
    });
  };

  const handleUseForDeployment = async () => {
    if (!payload || !selectedAccountNumber) return;
    setDeploying(true);
    setEncryptionError(null);
    try {
      await saveBrokerConnectionCredentials();
      navigate(brokerConnectionParams?.returnTo || "/deploy", { replace: true });
    } catch (e) {
      console.error("[SchwabSuccess] Error saving broker connection:", e);
      setEncryptionError("Failed to save Schwab connection. Please try again.");
      setDeploying(false);
    }
  };

  if (deploying && deployParams) {
    return (
      <div className="schwab-oauth-container">
        <div className="schwab-oauth-checkmark">✓</div>
        <h2 className="schwab-oauth-headline">Schwab Connected!</h2>
        <div className="schwab-oauth-auto-redirect">
          <p>Redirecting you back to deploy your strategy...</p>
          <div className="schwab-oauth-spinner"></div>
        </div>
      </div>
    );
  }

  if (error === "expired") {
    return (
      <div className="schwab-oauth-container">
        <div className="schwab-oauth-error-banner">Authorization took too long – please try again.</div>
        <div className="schwab-oauth-actions">
          <Link to="/deploy" className="schwab-oauth-reconnect-link">
            Back to Deploy
          </Link>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="schwab-oauth-container schwab-oauth-loading">
        <div className="schwab-oauth-spinner" />
        <div className="schwab-oauth-loading-text">Waiting for Schwab response…</div>
      </div>
    );
  }

  return (
    <div className="schwab-oauth-container schwab-oauth-fadein">
      <div className="schwab-oauth-icon-accent">
        <svg
          className="schwab-oauth-success-icon"
          width="56"
          height="56"
          viewBox="0 0 56 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="28" cy="28" r="28" fill="#2e7d32" opacity="0.13" />
          <circle cx="28" cy="28" r="20" fill="#4caf50" />
          <path d="M20.5 29.5L26 35L36 23" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="schwab-oauth-headline">
        {isManagedBotSpotFlow ? "Schwab connected!" : "Schwab connected"}
      </div>

      {isManagedBotSpotFlow && <div className="schwab-oauth-deployment-section">
        <button
          className="schwab-oauth-deployment-btn"
          onClick={handleUseForDeployment}
          disabled={deploying || !selectedAccountNumber}
          title={!selectedAccountNumber ? "Select an account to continue" : ""}
        >
          {deploying ? "Saving..." : brokerConnectionParams ? "Save broker connection" : "Use for Bot Deployment"}
        </button>
        <p className="schwab-oauth-deployment-description">
          Select an account, then {brokerConnectionParams ? "save it as a broker connection." : "automatically apply these credentials on the deploy page."}
        </p>
      </div>}

      {isManagedBotSpotFlow && !isAuthenticated && (
        <div className="schwab-oauth-error-banner" style={{ marginTop: 12 }}>
          Please sign in to load your Schwab accounts.
        </div>
      )}

      {isManagedBotSpotFlow && accountsLoading ? (
        <div style={{ marginTop: 12 }}>
          <div className="schwab-oauth-spinner" />
          <div className="schwab-oauth-loading-text">Loading your Schwab accounts…</div>
        </div>
      ) : isManagedBotSpotFlow && accountsError ? (
        <div className="schwab-oauth-error-banner" style={{ marginTop: 12 }}>
          {accountsError}{" "}
          <button type="button" className="schwab-oauth-reconnect-link" onClick={loadAccounts} style={{ border: 0, background: "transparent" }}>
            Retry
          </button>
        </div>
      ) : isManagedBotSpotFlow && accounts.length > 0 ? (
        <div style={{ marginTop: 14, width: "100%" }}>
          <label style={{ display: "block", color: "#c9d1d9", marginBottom: 6 }}>Select Account</label>
          <select
            value={selectedAccountNumber}
            onChange={(e) => setSelectedAccountNumber(e.target.value)}
            className="strategy-dropdown"
            style={{ width: "100%" }}
          >
            <option value="" disabled>
              Choose an account…
            </option>
            {accounts.map((acc) => {
              const accountNumber = acc?.accountNumber ?? acc?.account_number;
              const hashValue = acc?.hashValue ?? acc?.hash_value;
              const label = accountNumber ? truncateAccountNumber(accountNumber) : "Unknown Account";
              const key = String(hashValue || accountNumber || Math.random());
              return (
                <option key={key} value={String(accountNumber || "")}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      ) : null}

      {!isManagedBotSpotFlow && (
        <>
          <div className="schwab-oauth-divider" />

          <p className="schwab-oauth-deployment-description schwab-oauth-external-subhead">
            Copy this token JSON into your local Schwab token file.
          </p>

          <div className="schwab-oauth-token-panel">
            <div className="schwab-oauth-token-panel-header">
              <span>Token JSON</span>
              <button className="schwab-oauth-copy-btn" onClick={handleCopyJson} type="button" disabled={!decoded}>
                {jsonCopied ? "Copied" : "Copy JSON"}
              </button>
            </div>
            <pre className="schwab-oauth-json">{decoded ? JSON.stringify(decoded, null, 2) : "Invalid token JSON"}</pre>
            {showToast && <div className="schwab-oauth-copy-toast">Copied.</div>}
          </div>

          <div className="schwab-oauth-account-note">
            Set <code>SCHWAB_ACCOUNT_NUMBER</code> to the Schwab account you want LumiBot to use.
          </div>

          <details className="schwab-oauth-json-details">
            <summary>Encoded payload</summary>
            <div className="schwab-oauth-copy-row">
              <input
                className="schwab-oauth-copy-input"
                value={payload}
                readOnly
                aria-label="Schwab encoded token payload"
                onFocus={(e) => e.target.select()}
                tabIndex={0}
              />
              <button className="schwab-oauth-copy-btn" onClick={handleCopy} type="button">
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </details>
        </>
      )}

      {encryptionError && <div className="schwab-oauth-error-banner">{encryptionError}</div>}
    </div>
  );
}
